import { useEffect } from "react";

import { triggerLogin } from "authentication/actions";
import { widgetGuideUrl } from "common/constants/urls";
import { closeFullScreen, isFullscreen, openFullScreen } from "common/utilities/fullscreen";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { unauthenticate } from "domains/authentication/actions";
import { getUserRole } from "domains/authentication/selectors";
import { activateModal, deactivateModal } from "domains/modal/actions";
import { globalNavRemoteEvent, notificationsHostCommand } from "domains/nav/constants/eventTypes";
import { isCollectionAction } from "domains/nav/constants/menuItemPrefixes";
import {
  executeNotificationsRemoteEvent,
  NotificationsHostCommand,
  NotificationsRemoteEvent,
} from "domains/nav/notifications/notifications";
import type { GlobalNavRemoteEvent, RemoteEventPayloads } from "domains/nav/types/remoteEvent";
import { RemoteEvent } from "domains/nav/types/remoteEvent";
import { SubnavAction } from "domains/nav/types/subnavAction";
import { updateSearchQuery } from "domains/search/actions";
import { setTheme } from "domains/theme/actions";
import { useTheme } from "domains/theme/useTheme";
import { UserRole } from "entities/permissions/constants";

export const useRemoteEventHandler = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const userRole = useSelector(getUserRole);
  const theme = useTheme();

  useEffect(() => {
    const remoteEventHandlers: { [key in RemoteEvent]?: (value: RemoteEventPayloads[key]) => void } = {
      [RemoteEvent.AUTH_REQUEST_LOGOUT]: () => dispatch(unauthenticate({ isUserLogout: true })),
      [RemoteEvent.AUTH_REQUEST_SSI]: () => dispatch(triggerLogin()),
      [RemoteEvent.NAVIGATION_REQUEST]: ({ path }) => go(path.replace(import.meta.env.VITE_WEBSITE_URL, "")),
      [RemoteEvent.SEARCH_REQUEST]: ({ query }) => {
        dispatch(updateSearchQuery({ query, type: "topics" }));
        go("/search");
      },
      [RemoteEvent.SUBNAV_ACTION_REQUEST]: ({ value }) =>
        isCollectionAction(value)
          ? go(`/collection/${value.slice(18)}`)
          : void subnavActionHandlers[value as SubnavAction](),
    };

    const subnavActionHandlers = {
      [SubnavAction.ABOUT]: () => go("/about"),
      [SubnavAction.ALL_TOPICS]: () => go("/discover"),
      [SubnavAction.BOOKMARKS]: () => go("/bookmarks"),
      [SubnavAction.COACHMARK_SETTINGS]: () => dispatch(activateModal({ id: "CoachmarksSettings" })),
      [SubnavAction.CREATE]: () => go("/create"),
      [SubnavAction.CUSTOM_MAPS]: () => go(userRole === UserRole.Public ? "/shared" : "/create"),
      [SubnavAction.EMBED_MAPS_IN_YOUR_SITE]: () => open(widgetGuideUrl, "_blank", "noreferrer"),
      [SubnavAction.FEEDBACK]: () => open("mailto:intelligence@weforum.org", "_blank", "noreferrer"),
      [SubnavAction.FULLSCREEN]: () => (isFullscreen() ? closeFullScreen() : openFullScreen()),
      [SubnavAction.HOME]: () => go("/"),
      [SubnavAction.LANGUAGE]: () => dispatch(activateModal({ id: "ChangeLanguage" })),
      [SubnavAction.MONITOR]: () => go("monitor"),
      [SubnavAction.RESOURCES]: () => go("resources"),
      [SubnavAction.TERMS]: () => go("terms-and-conditions"),
      [SubnavAction.THEME]: () => dispatch(setTheme(theme === "dark" ? "light" : "dark")),
      [SubnavAction.WHATS_NEW]: () => dispatch(activateModal({ id: "WhatsNew" })),
    };

    const listener: EventListener = (event) => {
      const { _payload, _type } = (event as GlobalNavRemoteEvent).detail;
      remoteEventHandlers[_type]?.(_payload as never);
    };

    window.addEventListener(globalNavRemoteEvent, listener);
    return () => window.removeEventListener(globalNavRemoteEvent, listener);
  }, [dispatch, go, theme, userRole]);

  useEffect(() => {
    const handlers: Partial<Record<NotificationsHostCommand, () => void>> = {
      [NotificationsHostCommand.NOTIFICATIONS_CLOSE]: () => {
        dispatch(deactivateModal());
        executeNotificationsRemoteEvent(NotificationsRemoteEvent.NOTIFICATIONS_CLOSED, undefined);
      },
      [NotificationsHostCommand.NOTIFICATIONS_OPEN]: () => {
        dispatch(activateModal({ id: "Notifications" }));
        executeNotificationsRemoteEvent(NotificationsRemoteEvent.NOTIFICATIONS_OPENED, undefined);
      },
    };

    const listener: EventListener = (event) =>
      handlers[(event as CustomEvent<{ _type: NotificationsHostCommand }>).detail._type]?.();

    window.addEventListener(notificationsHostCommand, listener);
    return () => window.removeEventListener(notificationsHostCommand, listener);
  }, [dispatch]);
};
